import { ARTICLE_TOKENS } from '../design-tokens';
import { parseEmbedUrl } from '@lib/article-blocks/embed';
import type { SanityEmbedBlock } from '@lib/article-blocks/types';

const DEFAULT_VIDEO_TITLE = 'YouTube video';

const Caption = ({ text }: { text: string | undefined }) =>
  text ? (
    <figcaption
      className={`mt-2 font-body text-[14.5px] leading-[1.45] ${ARTICLE_TOKENS.bodyMuted}`}
    >
      {text}
    </figcaption>
  ) : null;

/**
 * Posts on X are linked rather than embedded: the widget script is heavy,
 * sets cookies before consent and breaks when the post is deleted, and a
 * static card degrades to nothing worse than a link.
 */
export const EmbedBlock = ({ value }: { value: SanityEmbedBlock }) => {
  const embed = parseEmbedUrl(value.url);
  if (!embed) return null;

  if (embed.provider === 'youtube') {
    return (
      <figure className="not-prose my-7" data-cy="embed-youtube">
        <div className="relative aspect-video overflow-hidden rounded-xl bg-black">
          <iframe
            src={embed.embedUrl}
            title={value.caption ?? DEFAULT_VIDEO_TITLE}
            loading="lazy"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            referrerPolicy="strict-origin-when-cross-origin"
            className="absolute inset-0 h-full w-full border-0"
          />
        </div>
        <Caption text={value.caption} />
      </figure>
    );
  }

  return (
    <figure className="not-prose my-7" data-cy="embed-x">
      <a
        href={embed.postUrl}
        target="_blank"
        rel="noopener noreferrer"
        className={`flex items-center justify-between gap-4 rounded-xl border px-5 py-4 font-body text-base no-underline ${ARTICLE_TOKENS.rule} ${ARTICLE_TOKENS.ink}`}
      >
        <span>{value.caption ?? 'View this post on X'}</span>
        <span
          className={`whitespace-nowrap text-sm ${ARTICLE_TOKENS.bodyMuted}`}
        >
          x.com ↗
        </span>
      </a>
    </figure>
  );
};
